import { Injectable } from '@angular/core';

import { BetState } from '@bpos/v1/my-bets';
import { FixtureStage, FixtureType } from '@cds/betting-offer';
import { PicksView } from '@cds/betting-offer/domain-specific';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { ConfirmationDialogButtonType, ConfirmationDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { emptyOdds } from '@frontend/sports/odds/feature';
import { BetslipV1Pick } from 'packages/sports/common/betslip/core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from 'packages/sports/common/betslip/core/picks/betslip-v2-option-market-pick';
import { BetslipV2StandardPick } from 'packages/sports/common/betslip/core/picks/betslip-v2-standard-pick';
import { CombinationPrevention } from 'packages/sports/common/betslip/core/picks/combo-prevention';
import { MinimumCombination } from 'packages/sports/common/betslip/core/picks/minimum-combination';
import { V1PickId, V2OptionMarketPickId } from 'packages/sports/common/betslip/core/picks/pick-id';
import { PriceType, ResultStatus } from 'packages/sports/common/betslip/core/picks/pick-models';
import { EditMyBetPicksPayload } from 'packages/sports/common/betslip/model/edit-mybet/edit-bet-init';
import { firstValueFrom } from 'rxjs';

import {
    IMyBetsBetWithOptionMarketInfo,
    IMyBetsBetWithResultInfo,
    MyBetsBetSlip,
    MyBetsListContext,
} from '../../my-bets-base/models/my-bets-viewmodels';
import { BetslipPicksRequestBuilderService } from './betslip-pick-request-builder.service';
import { MyBetsSourcePageService } from './mybets-source-page.service';

//
@Injectable()
export class MyBetsPicksFactoryService {
    protected logger: SportsRemoteLogger;

    constructor(
        protected mybetsSourcePageService: MyBetsSourcePageService,
        loggerFactory: LoggerFactory,
        private confirmationDialogService: ConfirmationDialogService,
        private siteCore: Sitecore,
        protected betslipPicksRequestBuilder: BetslipPicksRequestBuilderService,
        protected apiService: BettingOfferApi,
    ) {
        this.logger = loggerFactory.getLogger('MyBetsPicksFactoryService');
    }

    protected convertBetStateToResultedState(state: BetState): ResultStatus {
        switch (state) {
            case BetState.Canceled:
                return ResultStatus.Canceled;
            case BetState.Lost:
                return ResultStatus.Lost;
            case BetState.Open:
                return ResultStatus.Open;
            case BetState.Placed:
                return ResultStatus.Won;
            case BetState.WithHeld:
                return ResultStatus.Lost;
            case BetState.Won:
                return ResultStatus.Won;
            default:
                throw new Error('Unknown bet state!');
        }
    }

    protected buildPayload(response: PicksView, betslip: MyBetsBetSlip, myBetsListContext: MyBetsListContext): EditMyBetPicksPayload {
        const sourcePageState = this.mybetsSourcePageService.getSourcePageState(myBetsListContext);

        return {
            picksView: response,
            sourceBetslip: betslip,
            sourcePageState,
        };
    }

    protected createV1Pick(response: PicksView, bet: IMyBetsBetWithResultInfo): BetslipV1Pick {
        return this.createV1PickFromViewModel(bet);
    }

    protected createV2OptionMarketPick(response: PicksView, bet: IMyBetsBetWithOptionMarketInfo): BetslipV2OptionMarketPick {
        return this.createV2OptionMarketPickFromViewModel(bet);
    }

    private createV1PickFromViewModel(vm: IMyBetsBetWithResultInfo): BetslipV1Pick {
        const cutOffDate = new Date();
        const info = vm.resultInfo;
        const pick = new BetslipV1Pick();
        pick.resultStatus = this.convertBetStateToResultedState(vm.state);
        pick.id = new V1PickId(info.eventId.toString(), info.marketId, info.optionId);

        pick.sport = {
            id: vm.sportId,
            name: {
                name: vm.sportName,
                signature: '',
            },
        };

        pick.league = {
            id: info.leagueId,
            parentLeagueId: info.leagueId,
            name: {
                name: vm.leagueName,
                signature: '',
            },
        };

        pick.region = {
            id: 0,
            name: {
                name: '',
                signature: '',
            },
        };

        pick.event = {
            id: info.eventId.toString(),
            name: {
                name: vm.eventName,
                signature: '',
            },
            eventDate: vm.eventDate,
            cutOffDate,
            isLive: false,
            isPublished: false,
            groupId: null,
        };

        pick.market = {
            id: info.marketId,
            name: {
                name: info.marketName,
                signature: '',
            },
            comboPrevention: CombinationPrevention.AllCombo,
            minimumCombo: MinimumCombination.Single,
            isVisible: false,
            isClosed: true,
            templateId: 0,
            isBetBuilderEnabled: false,
        };
        pick.option = {
            id: info.optionId,
            name: {
                name: info.optionName,
                signature: '',
            },
            price: {
                id: info.optionId,
                type: PriceType.Fixed,
                isVisible: false,
                nativeOdds: {
                    decimals: vm.odds.european ?? 0,
                    fractional: {
                        numerator: vm.odds.british?.numerator,
                        denominator: vm.odds.british?.denominator,
                    },
                    moneyline: vm.odds.american ?? 0,
                },
                marketId: info.marketId,
            },
        };

        pick.acceptedPrice = {
            price: { ...pick.option.price },
            isManuallyAccepted: false,
        };

        return pick;
    }

    private createV2OptionMarketPickFromViewModel(vm: IMyBetsBetWithOptionMarketInfo): BetslipV2OptionMarketPick {
        const info = vm.optionMarketInfo;
        const pick = new BetslipV2StandardPick();
        pick.id = new V2OptionMarketPickId(info.fixtureCompoundId, info.optionMarketId, info.optionId, info.priceId);
        pick.resultStatus = this.convertBetStateToResultedState(vm.state);
        pick.fixture = {
            cutOffDate: new Date(),
            fixtureType: FixtureType.Standard,
            fixtureId: info.fixtureCompoundId,
            competitionGroupId: null,
            name: {
                name: vm.eventName,
                signature: '',
            },
            eventDate: vm.eventDate,
            region: {
                id: vm.leagueId,
                name: {
                    name: vm.leagueName,
                    signature: '',
                },
            },
            league: { id: vm.leagueId, name: { name: vm.leagueName, signature: '' } },
            sportId: 0,
            sportName: {
                name: '',
                signature: '',
            },
            stage: FixtureStage.Live,
            virtual: false,
            linkedEventIds: [],
        };

        pick.market = {
            id: info.optionMarketId,
            name: {
                name: vm.marketName,
                signature: '',
            },
            isEachWay: false,
            isVisible: false,
            isClosed: true,
            comboPrevention: CombinationPrevention.NoFixtureCombo,
            minimumCombo: 1,
            placeTerms: null,
            isBetBuilderEnabled: false,
        };

        pick.option = {
            id: info.optionId,
            name: {
                name: vm.optionName,
                signature: '',
            },
            isVisible: false,
            prices: [
                {
                    id: info.optionId,
                    isVisible: false,
                    marketId: info.optionMarketId,
                    type: PriceType.Fixed,
                    nativeOdds: emptyOdds,
                },
            ],
        };

        pick.acceptedPrice = {
            price: { ...pick.option.prices[0] },
            isManuallyAccepted: false,
        };

        return pick;
    }

    protected showErrorMessage(): ModalRef {
        return this.confirmationDialogService.showDialog({
            message: this.siteCore.betslip.editBet.errorOccurredWhileOpenEditBet,
            title: '',
            buttons: [
                {
                    buttonType: ConfirmationDialogButtonType.Ok,
                    buttonTitle: this.siteCore.betslip.editBet.confirmButtonTitle,
                    cssClass: '',
                },
            ],
        });
    }

    async createPayload(betslip: MyBetsBetSlip, myBetsListContext: MyBetsListContext): Promise<Nullable<EditMyBetPicksPayload>> {
        try {
            const request = this.betslipPicksRequestBuilder.buildRequestFromBets(betslip.bets);
            const response = await firstValueFrom(this.apiService.getPicksWithFallback(request));
            if (response) {
                return this.buildPayload(response, betslip, myBetsListContext);
            }
        } catch (e) {
            this.logger.error(e, 'Error while requesting picks with callback');
            this.showErrorMessage();
        }

        return null;
    }
}
